import { Maybe } from 'graphql/jsutils/Maybe'
import React, { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'

import { Icon } from '../../atoms'
import { UserTypeContextEnum, useUserTypeContext } from '../../contexts'
import { Container } from '../../layout'
import { Breadcrumb } from '../../molecules/breadcrumb/breadcrumb'
import AdminContact from '../../templates/client-request-cart/client-request-company-information/admin-contact/AdminContact'
import { SuggestionViewEnum } from '../../templates/new-client-request-cart/enums/SuggestionViewEnum.enum'
import {
  AdminOutputDto,
  ClientRequestStepOutputDto,
  HouseBookingTypeEnum,
} from '../../../lib/graphql'
import { Button } from '../../../shadcn/components/ui/button'
import { cn } from '../../../shadcn/utils'

export interface IClientRequestCartHeaderPropos {
  briefTitle: string | ReactElement
  stepData: ClientRequestStepOutputDto
  houseBookingType?: HouseBookingTypeEnum
  onPressOpenApp?: () => void
  stepNumber?: number
  admin?: Maybe<AdminOutputDto>
  view: SuggestionViewEnum
  setView: React.Dispatch<React.SetStateAction<SuggestionViewEnum>>
  setIsParameterModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const ClientRequestCartHeader = ({
  briefTitle,
  stepData,
  onPressOpenApp,
  stepNumber,
  admin,
  view,
  setView,
  setIsParameterModalOpen,
}: IClientRequestCartHeaderPropos) => {
  const { userType } = useUserTypeContext()

  return (
    <>
      <Container
        title={
          <div
            className={cn(
              'flex h-10 w-full flex-col items-center lg:gap-4',
              stepNumber && stepNumber > 1 && 'items-center',
            )}
          >
            <div className='flex h-10 w-full flex-col justify-start gap-y-4 md:flex-row md:items-center md:justify-between lg:gap-5'>
              <Breadcrumb
                className={cn(
                  'flex max-w-full items-center gap-2 md:overflow-hidden',
                )}
                olClassName='md:flex-nowrap md:truncate '
              >
                <Breadcrumb.Item
                  href={
                    userType === UserTypeContextEnum.USER
                      ? '/suggestions'
                      : '/bookings'
                  }
                >
                  <FormattedMessage
                    id='ui.ClientRequestCartHeader.mySpace'
                    defaultMessage='Mon espace'
                  />
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  className='text-ellipsis whitespace-nowrap'
                  current
                >
                  {briefTitle}
                </Breadcrumb.Item>
              </Breadcrumb>

              <div
                className={cn('flex gap-4', {
                  'lg:-mr-4': userType !== UserTypeContextEnum.OWNER,
                })}
              >
                {view && userType !== UserTypeContextEnum.OWNER && (
                  <>
                    <div className='hidden h-10 rounded-md lg:flex'>
                      <Button
                        onClick={() => setView(SuggestionViewEnum.LIST)}
                        variant='outline'
                        className={cn(
                          'h-10 gap-1 rounded-l-md rounded-r-none border fill-black p-3 text-sm',
                          view === SuggestionViewEnum.LIST
                            ? 'border fill-black text-black'
                            : 'border-grey-500 border-r-transparent text-grey-700',
                        )}
                      >
                        <Icon name='list' size={17} />
                        <FormattedMessage
                          id='ui.ClientRequestCartHeader.list'
                          defaultMessage='Liste'
                        />
                      </Button>
                      <Button
                        onClick={() => setView(SuggestionViewEnum.MAP)}
                        variant='outline'
                        className={cn(
                          'h-10 gap-1 rounded-l-none rounded-r-md border fill-black p-3 text-sm',
                          view === SuggestionViewEnum.MAP
                            ? 'border fill-black text-black'
                            : 'border-grey-500 border-l-transparent text-grey-700',
                        )}
                      >
                        <Icon name='map' size={17} />
                        <FormattedMessage
                          id='ui.ClientRequestCartHeader.map'
                          defaultMessage='Carte'
                        />
                      </Button>
                    </div>
                    <Button
                      onClick={() => setView(SuggestionViewEnum.MAP)}
                      variant='outline'
                      className={cn(
                        'flex h-10 gap-2 border fill-black p-3 px-4 text-sm text-black lg:hidden',
                      )}
                    >
                      <Icon name='map' size={15} />
                      <FormattedMessage
                        id='ui.ClientRequestCartHeader.map'
                        defaultMessage='Carte'
                      />
                    </Button>
                  </>
                )}
                <div className='flex gap-4'>
                  {onPressOpenApp && (
                    <Button
                      className='rounded-md border p-2'
                      onClick={onPressOpenApp}
                    >
                      <FormattedMessage
                        id='ui.ClientRequestCartHeader.openApp'
                        defaultMessage='Ouvrir mon app'
                      />
                    </Button>
                  )}
                  {userType === UserTypeContextEnum.OWNER && admin && (
                    <AdminContact admin={admin} />
                  )}
                  {userType === UserTypeContextEnum.USER &&
                    stepData.stepNumber <= 1 && (
                      <Button
                        variant='outline'
                        className='flex lg:hidden'
                        onClick={() => view && setIsParameterModalOpen(true)}
                      >
                        <FormattedMessage
                          id='ui.ClientRequestCartHeader.projectParameters'
                          defaultMessage='Paramètres du projet'
                        />
                      </Button>
                    )}
                </div>
              </div>
            </div>
          </div>
        }
        hasCustomCollapseIcon
        isCollapsible
      ></Container>
    </>
  )
}

export default ClientRequestCartHeader
